import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { workloadsApi } from '../services/workloadsService';
import { WorkloadDefinition } from '../services/types';
import { useAuth } from '../contexts/AuthContext';
import { TableSkeleton } from '../components/SkeletonLoaders';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import { useApiQuery } from '../hooks/useApiQuery';
import { useApiMutation } from '../hooks/useApiMutation';
import { isTestWorkload } from '../config/workloadCapabilities';
import WorkloadTrainabilityChip from '../components/workload/WorkloadTrainabilityChip';

export default function WorkloadsPage() {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workloadToDelete, setWorkloadToDelete] = useState<WorkloadDefinition | null>(null);
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const isAdmin = hasRole('admin');

  const { data: workloads, loading, error, refetch } = useApiQuery<WorkloadDefinition[]>(
    () => workloadsApi.list(isAdmin),
    [isAdmin]
  );

  const deleteMutation = useApiMutation<string, { workloadId: string }>(
    (id: string) => workloadsApi.delete(id),
    {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        setWorkloadToDelete(null);
        refetch();
      },
    }
  );

  const handleCreateWorkload = () => {
    navigate('/workloads/new');
  };

  const handleEditWorkload = (workloadId: string) => {
    navigate(`/workloads/${workloadId}/edit`);
  };

  const handleDeleteClick = (workload: WorkloadDefinition) => {
    setWorkloadToDelete(workload);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!workloadToDelete) return;
    await deleteMutation.mutate(workloadToDelete.id);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setWorkloadToDelete(null);
  };

  // Defensive against workload docs that are missing `category`: the seed
  // for the 'test' workload (infrastructure/data/075-data-seed-workloads.ps1)
  // has no category field, and without a guard the default branch crashes
  // the whole page with `undefined.toUpperCase()`. NC-431 follow-up.
  const getCategoryLabel = (category?: string) => {
    if (!category) return 'Unbekannt';
    switch (category) {
      case 'cam':
        return 'CAM';
      case 'bim':
        return 'BIM';
      case 'it':
        return 'IT Development';
      default:
        return category.toUpperCase();
    }
  };

  const getCategoryColor = (category?: string): 'primary' | 'secondary' | 'info' => {
    switch (category) {
      case 'cam':
        return 'primary';
      case 'bim':
        return 'secondary';
      case 'it':
        return 'info';
      default:
        return 'info';
    }
  };

  const displayError = error || deleteMutation.error;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Workload Management</Typography>
        <Box>
          <Tooltip title="Aktualisieren">
            <IconButton onClick={refetch} size="large" sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateWorkload}
            >
              Neuer Workload
            </Button>
          )}
        </Box>
      </Box>

      {displayError && (
        <ErrorMessage
          message={displayError}
          title="Fehler beim Laden der Workloads"
          onRetry={refetch}
        />
      )}

      {loading && <TableSkeleton rows={3} columns={1} />}

      {!loading && !error && (workloads || []).length === 0 && (
        <EmptyState
          title="Keine Workloads vorhanden"
          action={isAdmin ? { label: 'Workload erstellen', onClick: handleCreateWorkload } : undefined}
        />
      )}

      {!loading && !error && (workloads || []).length > 0 && (
        <Grid container spacing={3}>
          {(workloads || []).map((workload) => {
            const apps = workload.applications ?? [];
            return (
            <Grid item xs={12} md={6} lg={4} key={workload.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: workload.isActive ? 1 : 0.6,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {workload.name}
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip
                          label={getCategoryLabel(workload.category)}
                          color={getCategoryColor(workload.category)}
                          size="small"
                        />
                        <Chip
                          icon={workload.isActive ? <ActiveIcon /> : <InactiveIcon />}
                          label={workload.isActive ? 'Aktiv' : 'Inaktiv'}
                          size="small"
                          color={workload.isActive ? 'success' : 'default'}
                        />
                        {/* NC-1288 (Soll 5): platform-test workloads are marked so
                            an operator does not mistake the B2s probe workload for
                            a training offering. */}
                        {/* NC-1296 Soll 4: say WHY a workload cannot carry a
                            training yet, on the workload itself — the #1273
                            dead-end was discovering it from a 400 after filling
                            in the whole run form. */}
                        <WorkloadTrainabilityChip workload={workload} />
                        {/* NC-1296 Soll 3: the completeness driver. Shown ONLY on
                            a measured absence — `undefined` means the server could
                            not read the profile container, and rendering that as
                            "fehlt" would brand every workload as undocumented
                            during an outage. Not a gate (Spec §2): it informs. */}
                        {workload.hasProfile === false && (
                          <Tooltip title="Für diesen Workload ist noch kein Steckbrief hinterlegt — Zweck, Verantwortung und Nutzung sind nicht dokumentiert. Ueber den Steckbrief-Button nachtragen.">
                            <Chip
                              label="Steckbrief fehlt"
                              size="small"
                              color="warning"
                              variant="outlined"
                              data-testid="workload-profile-missing-chip"
                            />
                          </Tooltip>
                        )}
                        {workload.hasProfile === true && workload.profileStatus === 'draft' && (
                          <Tooltip title="Der Steckbrief ist angelegt, aber noch als Entwurf geführt — es fehlen Angaben.">
                            <Chip
                              label="Steckbrief Entwurf"
                              size="small"
                              color="default"
                              variant="outlined"
                              data-testid="workload-profile-draft-chip"
                            />
                          </Tooltip>
                        )}
                        {isTestWorkload(workload.id) && (
                          <Tooltip title="Test-Workload — dient dem Testen der Plattform, nicht der Schulung.">
                            <Chip
                              label="Test"
                              size="small"
                              color="warning"
                              variant="outlined"
                              data-testid="workload-test-chip"
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Button
                        size="small"
                        onClick={() => navigate(`/workloads/${encodeURIComponent(workload.id)}/steckbrief`)}
                      >
                        Steckbrief
                      </Button>
                      {isAdmin && (
                        <Box>
                          <Tooltip title="Bearbeiten">
                            <IconButton
                              size="small"
                              onClick={() => handleEditWorkload(workload.id)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Löschen">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(workload)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    {workload.description}
                  </Typography>

                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Anwendungen ({apps.length})
                    </Typography>
                    <Box mt={1}>
                      {apps.slice(0, 3).map((app) => (
                        <Chip
                          key={app.id}
                          label={app.displayName}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                      {apps.length > 3 && (
                        <Chip
                          label={`+${apps.length - 3} mehr`}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      VM-Größe: {workload.packerConfig?.vmSize ?? '—'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Workload löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie den Workload <strong>{workloadToDelete?.name}</strong> wirklich löschen?
            Dies ist eine Soft-Delete-Operation (isActive=false) und der Workload bleibt in der
            Datenbank für Audit-Zwecke erhalten.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteMutation.loading}>
            Abbrechen
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleteMutation.loading} autoFocus>
            {deleteMutation.loading ? 'Löschen...' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
